import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { Location } from '@angular/common';

export const leapInitialQueryParamsStorageKey = 'leapInitialQueryParams';

@Injectable({
	providedIn: 'root',
})
export class LeapUrlService {
	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private location: Location,
	) {}

	saveInitialQueryParamsInSessionStorage(queryParams: Params) {
		window.sessionStorage.setItem(
			leapInitialQueryParamsStorageKey,
			JSON.stringify(queryParams),
		);
	}

	getInitialQueryParamsFromSessionStorage(): Params {
		const queryParams = window.sessionStorage.getItem(
			leapInitialQueryParamsStorageKey,
		);

		if (!queryParams || queryParams === 'null') {
			return {};
		}

		return JSON.parse(queryParams);
	}

	/**
	 * Ensure that the current window's url has a query param called 'appSessionId' which is required to initialize
	 * the Leap SDK. It will create and replace the current url if necessary. A cleanup function is returned to revert
	 * the url back to its original state, or undefined is returned if a suitable url is not possible.
	 */
	insertLeapSdkFriendlyUrl() {
		const originalBaseUrl = this.getCurrBaseUrl();
		const originalQueryParams = this.getCurrQueryParams();

		const restoreUrlFn = () => {
			this.replaceCurrentUrl(
				this.constructUrl(originalBaseUrl, originalQueryParams),
			);
		};

		if (Object.hasOwnProperty.call(originalQueryParams, 'appSessionId')) {
			return restoreUrlFn;
		}

		const initialQueryParams = this.getInitialQueryParamsFromSessionStorage();

		if (!Object.hasOwnProperty.call(initialQueryParams, 'appSessionId')) {
			return undefined;
		}

		const sdkFriendlyUrl = this.constructUrl('leap', initialQueryParams);
		this.replaceCurrentUrl(sdkFriendlyUrl);

		return restoreUrlFn;
	}

	constructUrl(baseUrl: string, params: Params): string {
		const httpParams = new HttpParams({ fromObject: params });
		const queryString = httpParams.toString();
		return `${baseUrl}${queryString ? '?' + queryString : ''}`;
	}

	replaceCurrentUrl(url: string) {
		this.location.replaceState(url);
	}

	/**
	 * Returns the full initial url that was used when Closer In Leap first loaded
	 */
	getFullInitialUrl() {
		return this.constructUrl(
			`${window.location.origin}/leap`,
			this.getInitialQueryParamsFromSessionStorage(),
		);
	}

	private getCurrBaseUrl(): string {
		return this.router.url.split('?')[0];
	}

	private getCurrQueryParams(): Params {
		return this.route.snapshot.queryParams;
	}
}
